import { sortNodesByLineage } from './lib/util.js';
import {
  getPointFromAngles,
  getAnglesFromPoints,
} from './lib/geom';
import { DEFAULTS, titleYOffset, titleXOffset, DEFAULT_SIBLING_LINK_ANGLE } from './lib/constants';
import { IllegalMapOperationError } from '../../lib/custom_errors';
import { uuid } from '../../shared/lib/uuid.js';
import richText from './lib/rich-text';

class MapStore {

  constructor({ title, roles, access, settings }) {
    this.title = title || DEFAULTS.MAP_TITLE;
    this.roles = roles || {};
    this.access = access;
    this.settings = settings;
    this.disconnected = false;
    this.stopTheWorldError = false;
    this.viewOnlyTriggered = false;
    this.initEmpty();
  }

  initEmpty() {
    this.nodes = new Map([]);
    this.links = [];
    this.deleted = {
      nodes: new Map([]),
      links: [],
      perspectives: [],
      comments: [],
    };
    this.perspectives = [];
    this.presentations = [];
    this.attributes = {};
    this.regions = [];
    this.comments = [];
    this.versionNumber = 0;
  }

  getMapId() {
    return this.access.id;
  }

  getNodesSize() {
    return this.nodes.size;
  }

  setCardDraggingMode(bool) {
    if (!bool) {
      this.clearNodeCaches();
    }
    this._cardDraggingMode = bool;
  }

  clearNodeCaches() {
    for (const node of this.getNodesArray()) {
      node._cache = {};
    }
  }

  isCardDraggingMode() {
    return this._cardDraggingMode || false;
  }

  addNode({ id, title, titleMeta, summary, summaryMeta, x, y, height, isCollapsed, parentId, createdById, placeholder, orderIndex, childrenDetached, cx, cy, uh, uw, userHeight, userWidth, justification, layoutName, canonicalNodeUUID, externalIdentifier, librarySlug, isNewNode, attachedFile, backgroundColor, displayStyle, roles, attributes, linkStates }) {
    if (this.getNode(id)) {
      console.log('already added node: ', id);
      return;
    }

    if(!id) {
      id = uuid();
    }

    const node = new Node({ id, title, titleMeta, summary, summaryMeta, x, y, height, isCollapsed, parentId, createdById, orderIndex, childrenDetached, cx, cy, uh, uw, userHeight, userWidth, justification, layoutName, canonicalNodeUUID, externalIdentifier, librarySlug, isNewNode, attachedFile, backgroundColor, displayStyle, roles, attributes, linkStates }, this);
    this.nodes.set(id, node);

    if (parentId && this.getNode(parentId)) {
      this.getNode(parentId).addChild(id);
    }
    node.dirtyAncestors();

    return node.id;
  }

  addComment({ id, displayProperties, commentText, parentId, userId, createdTime, isDeleted }) {
    if (this.getComment(id)) {
      return;
    }
    const comment = new Comment({ id, displayProperties, commentText, parentId, userId, createdTime, isDeleted }, this);
    this.comments.push(comment);

    return comment;
  }

  updateMapDisplayProperties(displayProperties) {
    this.displayProperties = Object.assign(this.displayProperties || {},displayProperties);
  }

  // update legend perspective index and reorder perspectives
  updateLegendOrderIndexes(oldIndex, newIndex) {
    let sortedPerspectives = this.shiftArrayByIndexes(this.perspectives, oldIndex, newIndex).map((item, index) => {
      return new Perspective({ ...item, orderIndex: index });
    });
    this.perspectives = sortedPerspectives;
    return sortedPerspectives;
  }

  updateLegendPerspectives(perspectives) {
    this.perspectives = perspectives.map(o => {
      return new Perspective({ ...o });
    });
  }

  getMatchingCanonicalNodes(canonicalNodeUUID) {
    return this.filterNodes(node => node.canonicalNodeUUID == canonicalNodeUUID);
  }

  setTitle(title) {
    this.title = title;
  }

  getTitle() {
    return this.title;
  }

  setBackgroundColor(color) {
    this.backgroundColor = color;
  }

  getBackgroundColor() {
    return this.backgroundColor || 'gray1';
  }

  getNode(id) {
    return this.nodes.get(id);
  }

  getNodeByTitle(title) {
    return this.findNode(node => node.title === title);
  }

  getLink(id) {
    return this.links.find(link => link.id === id);
  }
  getLinks() {
    return this.links;
  }

  getLinksBetweenNodes(fromNodeId, toNodeId) {
    const linksBetweenNodes = this.links.filter( l => {
      return (l.fromNodeId === fromNodeId && l.toNodeId === toNodeId)
      || (l.fromNodeId === toNodeId && l.toNodeId === fromNodeId);
    });

    return linksBetweenNodes.sort((a, b) => {
      return Math.abs(b.angleEndToR) - Math.abs(a.angleEndToR);
    });
  }

  // sort ids by deepest nested first
  sortByDepth(ids) {
    const depth = {};
    for (const id of ids) {
      if (id !== undefined && this.getNode(id)) {
        depth[id] = this.getNode(id).getAbsoluteDepth();
      }
    }
    return ids.sort((b, a) => {
      return depth[a] - depth[b];
    });
  }

  // sort ids by deepest nested first
  sortLinksByDepth(ids) {
    const depth = {};
    for (const id of ids) {
      const link = this.getLink(id);
      if (id !== undefined && link) {
        depth[id] = Math.max(link.fromNode(id).ancestors().length,link.toNode(id).ancestors().length);
      }
    }

    return ids.sort((b, a) => {
      return depth[a] - depth[b];
    });
  }

  shiftArrayByIndexes(arr, oldIndex, newIndex) {
    if (newIndex >= arr.length) {
      var k = newIndex - arr.length;
      while ((k--) + 1) {
        arr.push(undefined);
      }
    }
    arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0]);
    return arr;
  }

  updateAccess(updates) {
    for (let key in updates) {
      this.access[key] = updates[key];
    }
  }

  isDisconnected() {
    return this.disconnected;
  }

  setViewOnly() {
    this.viewOnlyTriggered = true;
  }

  unsetViewOnly() {
    this.viewOnlyTriggered = false;
  }

  isViewOnly() {
    if (!this.access || this.access.permission != 'edit' || this.isDisconnected() || this.stopTheWorldError || this.viewOnlyTriggered) {
      return true;
    }
    return false;
  }

  canCommentOnly() {
    if (this.access && this.access.permission === 'comment' && !this.isDisconnected() && !this.stopTheWorldError && !this.viewOnlyTriggered) {
      return true;
    }
    return false;
  }

  deleteFromAllPerspectives({nodeIds, linkIds}) {
    const perspectives = this.getPerspectives();
    if (perspectives && perspectives.length) {
      for (const p of perspectives) {

        if (nodeIds) {
          for (const id of nodeIds) {
            p.removeMemberNode(id);
          }
        }

        if (linkIds) {
          for (const id of linkIds) {
            p.removeMemberLink(id);
          }
        }
      }
    }
  }

  undeleteFromAllPerspectives({nodeIds,linkIds}) {
    const perspectives = this.getPerspectives();
    if (perspectives && perspectives.length) {
      for (const p of perspectives) {

        if (nodeIds) {
          for (const id of nodeIds) {
            p.undeleteMemberNode(id);
          }
        }

        if (linkIds) {
          for (const id of linkIds) {
            p.undeleteMemberLink(id);
          }
        }
      }
    }
  }

  deleteNodes({ nodeIds, linkIds }) {
    nodeIds = nodeIds || [];
    linkIds = linkIds || [];

    const deletedNodeIds = [];
    const deletedLinkIds = [];
    const deletedLinkNodesMap = {};

    const _deleteNode = nodeId => {
      const deletedNode = this.nodes.get(nodeId);
      if (!deletedNode) return;

      deletedNode.dirtyAncestors();
      const link = this.links.find(l => l.linkNodeId === nodeId);
      if (link) {
        link.setLinkNodeId(null);
        deletedLinkNodesMap[nodeId] = link.id;
        link.setLinkNodeId(null);
      }
      this.deleted.nodes.set(deletedNode.id, deletedNode);
      deletedNodeIds.push(deletedNode.id);
      this.nodes.delete(nodeId);

      const orphanedLinks = this.links.filter(l => {
        return (l.fromNodeId == nodeId || l.toNodeId == nodeId);
      });

      orphanedLinks.forEach(ol => {
        const i = this.links.findIndex(l => l.id == ol.id);
        if (i == -1) return;
        this.deleted.links.push(this.links[i]);
        deletedLinkIds.push(this.links[i].id);
        this.links.splice(i, 1);
        if (ol.linkNodeId) {
          _deleteNode(ol.linkNodeId);
        }
      });

      deletedNode.children().forEach(child => {
        _deleteNode(child.id);
      });
    };

    for (let nodeId of nodeIds) {
      _deleteNode(nodeId);
    }

    for (let linkId of linkIds) {
      if (this.deleteLink(linkId)) {
        deletedLinkIds.push(linkId);
      }
    }

    const filterStore = withStore => {
      const { _store, ...withoutStore } = withStore;
      return withoutStore;
    };

    if (!deletedNodeIds.length && !deletedLinkIds.length) return;

    return {
      deletedNodeIds,
      deletedLinkIds,
      deletedLinkNodesMap,
    };
  }

  undeleteNodes({ deletedNodeIds, deletedLinkIds, deletedLinkNodesMap }) {
    const undeletedNodeIds = [];
    const undeletedLinkIds = [];

    let deletedNodes = deletedNodeIds.map(id => Array.from(this.deleted.nodes.values()).find(n => n.id === id))
                                     .filter(n => n instanceof Node);

    if (deletedNodes && deletedNodes.length) {
      deletedNodes = sortNodesByLineage({nodes: deletedNodes, store: this });

      for (const undeletedNode of deletedNodes) {
        const nodeId = undeletedNode.id;

        undeletedNode.dirtyAncestors();

        if (undeletedNode.parentId) {
          if (!this.getNode(undeletedNode.parentId)) {
            continue;
          }
        }
        this.nodes.set(nodeId, undeletedNode);
        this.deleted.nodes.delete(undeletedNode.id);
        undeletedNodeIds.push(nodeId);
      }
    }

    for (const linkId of deletedLinkIds) {
      const linkIndex = this.deleted.links.findIndex(l => l.id == linkId);
      if (linkIndex == -1) continue;

      const deletedLink = this.deleted.links[linkIndex];

      if (this.getNode(deletedLink.toNodeId) &&
        this.getNode(deletedLink.fromNodeId)) {

        this.links.push(deletedLink);
        undeletedLinkIds.push(linkId);
        this.deleted.links.splice(linkIndex, 1);
      }
    }

    Object.keys(deletedLinkNodesMap).forEach(linkNodeId => {
      const linkId = deletedLinkNodesMap[linkNodeId];
      const link = this.links.find(l => l.id === linkId);
      if (link) {
        link.setLinkNodeId(linkNodeId);
      }
    });

    if (!undeletedNodeIds.length && !undeletedLinkIds.length) return;

    return {
      undeletedNodeIds,
      undeletedLinkIds,
    };
  }

  deleteLink(linkId) {
    const deletedLinkIds = [];
    const deletedNodeIds = [];
    const lnIndex = this.links.findIndex(l => l.id === linkId);
    if (lnIndex == -1) return;

    const link = this.getLink(linkId);
    if (!link) {
      return;
    }

    if (link.linkNode()) {
      link.linkNode().dirtyDescendants();
    }

    deletedLinkIds.push(this.links[lnIndex].id);
    this.links.splice(lnIndex, 1);
    this.deleted.links.push(link);

    return linkId;
  }

  addLink({
    id,
    fromNodeId,
    toNodeId,
    linkNodeId,
    direction,
    angleStartToR,
    angleEndToR,
    color,
    lineType = DEFAULTS.LINK_TYPE,
    lineWidth = DEFAULTS.LINK_WIDTH,
    lineCurvature = DEFAULTS.LINK_CURVATURE,
    createdById,
  }) {
    if (this._validateLinkedNodes(fromNodeId, toNodeId, linkNodeId)) {
      const link = new Link({ id, fromNodeId, toNodeId, linkNodeId, direction, angleStartToR, angleEndToR, color, lineType, lineWidth, lineCurvature, createdById }, this);
      this.links.push(link);
      return link.id;
    } else {
      throw new IllegalMapOperationError("Tried to add illegal link");
    }

    return null;
  }

  _validateLinkedNodes(fromNodeId, toNodeId, linkNodeId) {
    if (!fromNodeId) {
      console.log('fromNode cannot be null');
      return false;
    }
    if (!toNodeId) {
      console.log('toNode cannot be null');
      return false;
    }
    if (fromNodeId == toNodeId) {
      console.log('from node cannot be to node');
      return false;
    }

    if (fromNodeId == linkNodeId || toNodeId == linkNodeId) {
      console.log("link nodes cannot be the same link's from/to nodes");
      return false;
    }
    if (!this.getNode(fromNodeId)) {
      console.log('from node does not exist');
      return false;
    }
    if (!this.getNode(toNodeId)) {
      console.log('to node does not exist');
      return false;
    }
    return true;
  }


  // remove references to non-existent parent nodes
  sanitizeBrokenParents() {
    for (const node of this.getNodes()) {
      if (node.parentId && !this.getNode(node.parentId)) {
        console.log('node ', node.id, ' missing parent node: ', node.parentId);
        node.adoptParent(null);
      }
    }
  }

  // remove references to non-existent link nodes
  sanitizeBrokenLinkNodes() {
    for (const link of this.links) {
      if (link.linkNodeId && !this.getNode(link.linkNodeId)) {
        console.log('link', link.id, ' missing link node: ', link.linkNodeId);
        link.setLinkNodeId(null);
      }
    }
  }

  // keep all nodes within the canvas boundary
  sanitizeOutofBoundsNodes() {
    for (const node of this.getNodes()) {

      if (node.parentId) {
        continue;
      }
      if (node.link()) {
        continue;
      }

      if (node.x < this.settings.canvas.minX) {
        node.x = node.absolutePosition.x = this.settings.canvas.minX + 250;
      }
      if (node.y < this.settings.canvas.minY) {
        node.y = node.absolutePosition.y = this.settings.canvas.minY + 250;
      }

      if (node.x > this.settings.canvas.maxX) {
        node.x = node.absolutePosition.x = this.settings.canvas.maxX - 250;
      }
      if (node.y > this.settings.canvas.maxY) {
        node.y = node.absolutePosition.y = this.settings.canvas.maxY - 250;
      }
    }
  }

  // flag all nodes inside a collapsed node as hidden
  initializeHiddenStates(nodes) {
    if (!nodes) {
      nodes = this.getNodes();
    }
    for (const n of nodes) {
      if (n.getIsCollapsed()) {
        n.setDescendantsHidden(true);
      } else if (n.hidden) {
        n.setDescendantsHidden(true);
      } else {
        n.setDescendantsHidden(false);
      }
    }
  }

  undeleteLink(linkId) {
    const linkIndex = this.deleted.links.findIndex(l => l.id === linkId);
    if (linkIndex === -1) {
      console.log("undeleted link not here", linkId);
    }
    const deletedLink = this.deleted.links.find(l => l.id === linkId);
    if (deletedLink) {
      this.links.push(deletedLink);
      this.deleted.links.splice(linkIndex, 1);

      if (deletedLink.linkNode()) {
        deletedLink.linkNode().dirtyDescendants();
      }
    }
  }

  topNodes() {
    // sort link nodes last so they end up on top
    const nodes = this.filterNodes(n => !n.parentId);
    const sortedIds = this.sortByDepth(nodes.map(n => n.id)).reverse();
    return sortedIds.map(id => this.getNode(id));
  }

  // Finds the most recent common ancestor of a list of nodes.
  // If there is no common ancestor (except for the top-level), returns null.
  commonAncestor(nodes) {
    const _commonAncestorPair = (n1, n2) => {
      if (n1 === null || n2 === null) {
        return null;
      } else {
        const lineage1 = n1.getLineage();
        const lineage2 = n2.getLineage();
        if (lineage1.includes(n2)) {
          return n2;
        } else if (lineage2.includes(n1)) {
          return n1;
        } else {
          const common = lineage1.find(ancestor => lineage2.includes(ancestor));
          // find returns undefined if it can't find anything, but we want null
          return common ? common : null;
        }
      }
    };

    if (nodes.length >= 0) {
      return nodes.reduce(_commonAncestorPair, nodes[0]);
    } else {
      return null;
    }
  }

  getNodes() {
    return this.nodes.values();
  }
  getLinkNodes(){
    return this.links.map(link => {
      if (link.linkNodeId) {
        return this.getNode(link.linkNodeId);
      }
    });
  }

  getNodesArray() {
    return Array.from(this.nodes.values());
  }

  filterNodes(test) {
    const filtered = [];
    for (let pair of this.nodes) {
      const value = pair[1];
      if (test(value)) filtered.push(value);
    }
    return filtered;
  }

  findNode(test) {
    for (let pair of this.nodes) {
      const value = pair[1];
      if (test(value)) return value;
    }
  }

  getLinks() {
    return this.links;
  }

  getNodePositions(nodeIds) {
    const nodePositions = [];
    for (let id of nodeIds) {
      const node = store.getNode(id);
      if (node) {
        nodePositions.push({
          uuid: node.id,
          x: node.x,
          y: node.y,
          height: node.height,
          absolutePosition: Object.assign({}, node.absolutePosition),
          orderIndex: node.orderIndex,
          parentId: node.parentId,
        });
      }
    }
    return nodePositions;
  }

  getLinkAngles(linkIds) {
    const linkAngles = [];
    for (let id of linkIds) {
      const link = store.getLink(id);
      if (link) {
        linkAngles.push({
          uuid: link.id,
          angleStartToR: link.angleStartToR,
          angleEndToR: link.angleEndToR,
        });
      }
    }
    return linkAngles;
  }

  getPerspectives() {
    return this.perspectives.filter(p => !Boolean(p.hidden));
  }

  getAllPerspectives() {
    return this.perspectives;
  }

  /*
    annotationStyle can be: popout, in_card
  */
  getPerspectiveAnnotationStyle() {
    if (this.displayProperties && this.displayProperties.annotationStyle) {
      return this.displayProperties.annotationStyle;
    }
    return DEFAULTS.ANNOTATION_STYLE;
  }

  getPerspectiveFromTitle(title) {
    const perspectives = this.getPerspectives();
    for (const p of perspectives) {
      if (p.getTitle() && p.getTitle() == title) {
        return p;
      }
    }
  }

  getPerspectiveFromTitleWithoutPointNode(title) {
    const perspectives = this.getPerspectives();
    for (const p of perspectives) {
      if (!p.pointNodeUUID && p.getTitle() && p.getTitle() == title) {
        return p;
      }
    }
  }

  getUsedPerspectiveColors() {
    //  get unique colors
    return Array.from(new Set(this.perspectives.map(perspective => perspective.color)));
  }

  getPerspective(id) {
    return this.perspectives.find(p => p.id === id);
  }

  undeletePerspective(id) {
    const p = this.deleted.perspectives.find(p => p.id === id);
    if (p) {
      this.deleted.perspectives = this.deleted.perspectives.filter(p => p && p.id !== id);
      this.addPerspective(p);
      this.sortPerspectives();
    }
  }

  getPerspectiveFromPointNode(pointNodeUUID) {
    return store.perspectives.find(p => p.pointNodeUUID == pointNodeUUID);
  }

  addPerspective({ id, color, label, pointNodeUUID, orderIndex, links, nodes, members }) {
    if (id && this.getPerspective(id) !== undefined) {
      // avoid adding duplicate perspectives
      return;
    }
    if (orderIndex === undefined) {
      if (this.perspectives.length) {
        // generate next order index
        orderIndex = this.perspectives[this.perspectives.length - 1].orderIndex + 1;
      } else {
        orderIndex = 1;
      }
    }
    if (members) {
      if (members.nodes) {
        nodes = members.nodes;
      }
      if (members.links) {
        links = members.links;
      }
    }

    if (this.getPerspectiveFromPointNode(pointNodeUUID)) {
      // last line of defense to ensure that no 2 perspectives share a point node
      pointNodeUUID = null;
    }
    const newP = new Perspective({ id, color, label, pointNodeUUID, links, nodes, orderIndex });
    this.perspectives.push(newP);
    return newP.id;
  }

  sortPerspectives() {
    store.perspectives = store.perspectives.sort((a,b) => { return a.orderIndex - b.orderIndex;});
  }

  deletePerspective(id) {
    const perspective = this.getPerspective(id);
    this.perspectives = this.perspectives.filter(p => p.id !== id);
    this.deleted.perspectives.push(perspective);
  }

  deletePerspectivePoint(id) {
    const perspective = this.perspectives.find(p => p.id === id);
    if (perspective) {
      const pointId = perspective.pointNodeUUID;
      if (pointId) {
        perspective.pointNodeUUID = null;
        //this.deleteNodes({ nodeIds: [pointId] });
      }
    }
  }

  getPresentation() {
    return this.presentations[0];
  }

  addPresentation({ id, title, createdByUserId, waypoints }) {
    const presentation = new Presentation({
      id,
      title,
      createdByUserId,
      waypoints,
    }, this);

    this.presentations.push(presentation);
  }

  addRegion({ id, title, color, top, left, bottom, right }) {
    if (this.getRegion(id)) {
      console.log('already added region: ', id);
      return;
    }
    const region = new Region({ id, title, color, top, left, bottom, right });
    this.regions.push(region);
    return region;
  }

  deleteRegion({ id }) {
    this.regions = this.regions
      .filter(region => region.id !== id);
  }

  getRegion(id) {
    return this.regions.find( region => region.id === id );
  }

  getRegions() {
    return this.regions;
  }

  getComment(id) {
    return this.comments.find(comment => comment.id === id);
  }

  getCommentsNearPosition(position) {
    const buffer = 10;
    return this.comments.filter(comment => {
      const pos = comment.getPosition();
      return !comment.isDeleted && pos && (pos.x >= position.x - buffer && pos.x <= position.x + buffer ) && (pos.y >= position.y - buffer && pos.y <= position.y + buffer);
    });
  }

  totalCommentCount() {
    return this.comments.filter(comment => !comment.isDeleted).length;
  }

  getOpenCommentCount() {
    return this.allTopLevelComments('Open').length;
  }

  getResolvedCommentCount() {
    return this.allTopLevelComments('Resolved').length;
  }

  topMapComments() {
    return this.allTopLevelComments('Open').filter(comment => comment.displayProperties.target === 'map');
  }

  allTopLevelComments(filter) {
    const comments = this.comments.filter(comment => !comment.parentId && !comment.isPurged());

    if (filter === 'Open') {
      return comments.filter(comment => !comment.isResolved());
    }

    if (filter === 'Resolved') {
      return comments.filter(comment => comment.isResolved());
    }

    return comments;
  }

  nodeTopComments(nodeId) {
    return this.allTopLevelComments('Open').filter(comment => comment.displayProperties.target === 'node' && comment.displayProperties.mapNodeUUID === nodeId);
  }

  getReplyComments(commentId) {
    return this.comments.filter(comment => comment.parentId && comment.parentId === commentId && !comment.isPurged());
  }

  getCommentNode(id) {
    const comment = this.getComment(id);
    if (!comment) return;

    const _getCommentNodeId = (comment) => {
      if (comment.getTarget() === 'map') return;
      if (comment.getTarget() === 'node') return comment.displayProperties.mapNodeUUID;
      if (comment.getTarget() === 'reply') {
        const parent = this.getComment(comment.getParentId());
        if (!parent) return;
        return _getCommentNodeId(parent);
      }
    };

    const nodeId = _getCommentNodeId(comment);
    if (!nodeId) return;
    const node = this.getNode(nodeId);
    if (!node) return;

    return node;
  }

  getCommentSelection(id) {
    const comment = this.getComment(id);
    if (!comment) return;

    const _getCommentSelectionIds = (comment) => {
      if (comment.getTarget() === 'map' && comment.displayProperties.selection) return comment.displayProperties.selection;
      if (comment.getTarget() === 'node') return;
      if (comment.getTarget() === 'reply') {
        const parent = this.getComment(comment.getParentId());
        if (!parent) return;
        return _getCommentSelectionIds(parent);
      }
    };

    const selection = _getCommentSelectionIds(comment);

    return selection;
  }

  doesRole(role) {
    return Object.keys(this.roles).includes(role);
  }

  getRoleAttribute(role, name) {
    if (this.roles[role]) {
      return this.roles[role].attributes.find(a => a.name === name);
    }
  }

  getAllAttributes() {
    let attributes = [];
    for (const node of this.getNodes()) {
      attributes = attributes.concat(node.getAttributes());
    }
    return attributes;
  }

  getAttributeFilterObj() {
    const attributes = {};
    for (const node of this.getNodes()) {
      for (const attr of node.getAttributes() || []) {
        if (!attributes[attr.name]) {
          attributes[attr.name] = { count: 1, values: {} };
        } else {
          attributes[attr.name].count++;
        }

        if (!attributes[attr.name].values[attr.value]) {
          attributes[attr.name].values[attr.value] = { count: 1 };
        } else {
          attributes[attr.name].values[attr.value].count++;
        }
      }
    }

    return attributes;
  }

  addAttribute(name, orderIndex) {
    if (!(name && name.length)) {
      return;
    }
    if (this.getAttribute(name)) {
      return;
    }
    this.attributes[name] = {
      orderIndex: orderIndex || 0,
      values: {},
    };
    return true;
  }

  removeAttribute(name) {
    if (!(name && name.length)) {
      return;
    }
    if (!this.getAttribute(name)) {
      return;
    }
    delete this.attributes[name];
    return true;
  }

  getAttribute(name) {
    return this.attributes[name];
  }

  addAttributeValue(name, value, orderIndex) {
    if (!(name && name.length)) {
      return;
    }
    if (!(value && value.length)) {
      return;
    }
    const attr = this.getAttribute(name);
    if (!attr) {
      return;
    }
    if (attr.values[value]) {
      return;
    }
    attr.values[value] = {
      orderIndex: orderIndex || 0,
    };
    return true;
  }

  removeAttributeValue(name, value) {
    if (!(name && name.length)) {
      return;
    }
    if (!(value && value.length)) {
      return;
    }
    const attr = this.getAttribute(name);
    if (!attr) {
      return;
    }
    if (!attr.values[value]) {
      return;
    }
    delete attr.values[value];
    return true;
  }

  load(data) {
    data.links = data.links || [];
    data.nodes = data.nodes || [];
    data.perspectives = data.perspectives || [];
    data.presentations = data.presentations || [];
    data.attributes = data.attributes || {};
    data.comments = data.comments || [];
    data.deleted = data.deleted || {};
    data.deleted.nodes = data.deleted.nodes || [];
    data.deleted.links = data.deleted.links || [];
    data.deleted.perspectives = data.deleted.perspectives || [];
    data.deleted.presentations = data.deleted.presentations || [];
    data.deleted.comments = data.deleted.comments || [];

    data.nodes = data.nodes.map(n => Object.assign({}, n, { title: n.title }));
    // sort nodes by lineage to add parents before children
    data.nodes = sortNodesByLineage({ nodes: data.nodes });

    this.initEmpty();

    data.nodes.forEach(n => {
      try {
        if (n.childrenPosition) {
          n.cx = n.childrenPosition.x;
          n.cy = n.childrenPosition.y;
          n.uh = n.childrenPosition.userHeight;
          n.uw = n.childrenPosition.userWidth;
        }
        this.addNode(n);
      } catch (e) {
        if (e instanceof IllegalMapOperationError) {
          // swallow
        } else {
          throw e;
        }
      }
    });
    data.links.forEach(l => {
      try {
        this.addLink(l);
      } catch (e) {
        if (e instanceof IllegalMapOperationError) {
          // swallow
        } else {
          throw e;
        }
      }
    });
    data.comments.forEach(c => {
      try {
        this.addComment(c);
      } catch(e) {
        if (e instanceof IllegalMapOperationError) {
          // swallow
        } else {
          throw e;
        }
      }
    });
    this.updateMapDisplayProperties(data.display_properties);

    data.perspectives.forEach(p => {
      this.addPerspective(p);
    });

    data.presentations.forEach(presentation => {
      this.addPresentation(presentation);
    });

    (data.regions || []).forEach(region => {
      if (region.uuid && !region.id) {
        console.warn('we dont have an id for a region');
        region.id = region.uuid;
      }
      this.addRegion(region);
    });

    this.sortPerspectives();

    this.sanitizeBrokenParents();
    this.sanitizeBrokenLinkNodes();
    this.sanitizeOutofBoundsNodes();
    this.initializeHiddenStates();

    const deletedNodes = data.deleted.nodes.map(n => new Node(n, this));
    deletedNodes.forEach(n => this.deleted.nodes.set(n.id, n));
    const deletedLinks = data.deleted.links.map(l => new Link(l, this));
    this.deleted.links.splice(0, this.deleted.links.length, ...deletedLinks);

    const deletedPerspectives = data.deleted.perspectives.map(p => new Perspective(p));
    this.deleted.perspectives.splice(0, this.deleted.perspectives.length, ...deletedPerspectives);

    const deletedComments = data.deleted.comments.map(c => new Comment(c, this));
    this.deleted.comments.splice(0, this.deleted.comments.length, ...deletedComments);

  }

  dump() {
    const filterNode = node => {
      // remove the _store property
      const { _store, ...newNode } = node;
      return newNode;
    };
    const filterLink = link => {
      // remove the _store property
      const { _store, ...newLink } = link;
      return newLink;
    };
    return {
      id: window.map_id,
      access: this.access,
      presentations: this.presentations.map(p => p.dump()),
      attributes: this.attributes || {},
      perspectives: this.perspectives|| [],
      nodes: Array.from(this.getNodes()).map(filterNode),
      links: this.links.map(filterLink),
      deleted: {
        nodes: Array.from(this.deleted.nodes.values()).map(filterNode),
        links: this.deleted.links.map(filterLink),
        perspectives: this.deleted.perspectives,
      },
      title: '' + this.title,
      regions: this.regions.map(r => r.dump()),
      comments: this.comments.map(c => c.dump()),
    };
  }

  dumpJSON() {
    return JSON.stringify(this.dump());
  }
}

class Node {

  constructor({ id, title, titleMeta, summary, summaryMeta, x, y, absolutePosition, height, isCollapsed, hidden, parentId, createdById, orderIndex, layoutName, childrenIds, childrenDetached, cx, cy, uh, uw, userWidth, justification, userHeight, canonicalNodeUUID, externalIdentifier, librarySlug, isNewNode, attachedFile, backgroundColor, displayStyle, roles, attributes, linkStates }, _store) {

    if (!_store) throw new Error("we need a store");

    this._store = _store;
    this._cache = {};
    this.id = id || uuid();
    this.title = title;
    this.titleMeta = titleMeta;
    this.summary = summary;
    this.summaryMeta = summaryMeta;
    this.parentId = parentId || null;
    this.absolutePosition = absolutePosition || { x, y };
    this.orderIndex = orderIndex || 0;
    this.x = parseFloat(x) || 0;
    this.y = parseFloat(y) || 0;
    this.createdById = createdById;
    this.isCollapsed = isCollapsed || false;
    this.hidden = hidden || false;
    this.height = height || 0;
    this.layoutName = layoutName || 'layout-row';
    this.childrenIds = childrenIds || [];
    this.childrenPosition = { x: cx, y: cy, userWidth: uw, userHeight: uh };
    this.collapsedChildrenPosition = { x: 0, y: 0 };
    this.userHeight = userHeight;
    this.userWidth = userWidth;
    this.justification = justification || 'left';
    this.childrenDetached = childrenDetached;
    this.canonicalNodeUUID = canonicalNodeUUID;
    this.externalIdentifier = externalIdentifier;
    this.librarySlug = librarySlug;
    this.isNewNode = isNewNode;
    this.attachedFile = attachedFile;
    this.backgroundColor = backgroundColor;
    this.displayStyle = displayStyle;
    this.roles = roles || [];
    this.attributes = attributes || {};
    this.linkStates = linkStates || {};
    this._dirty = true;
  }

  clean() {
    this._dirty = false;
  }

  dirty() {
    this._dirty = true;
  }

  dirtyAncestors() {
    this.getLineage().forEach(node => {
      node.dirty();
    });
  }

  dirtyDescendants() {
    this.dirty();
    this.descendants().forEach(node => {
      node.dirty();
    });
  }

  setZoomLevelHidden(zoomLevel) {
    this.zoomLevelHidden = zoomLevel;
  }

  getZoomLevelHidden() {
    return this.zoomLevelHidden;
  }

  children() {
    const children = this.childrenIds
      // move from ids to nodes
      .map(id => this._store.getNode(id))
      // toss out the nulls and undefineds
      .filter(n => n instanceof Node)
      .sort((a, b) => a.orderIndex - b.orderIndex);
    if (children) {
      return children;
    }
    return [];
  }

  lastChild() {
    const children = this.children();
    if (!children.length) return;
    return children[ children.length - 1 ];
  }

  unsetIsNewNode() {
    delete this.isNewNode;
  }

  // get all parents up to the top level and including the current node
  getLineage() {
    const ancestors = this.ancestors();
    ancestors.unshift(this);
    return ancestors.reverse();
  }

  getParent() {
    if (!this.parentId) {
      return null;
    }
    const parent = this._store.getNode(this.parentId);
    return parent;
  }

  isChild() {
    return Boolean(this.parentId);
  }

  isGrandChild() {
    if (this.parentId) {
      return Boolean(this._store.getNode(this.parentId).parentId);
    } else {
      return false;
    }
  }

  /* get all parents and their parents */
  ancestors() {
    const nodes = [];
    const getAncestors = node => {
      const parent = node.getParent();
      if (parent) {
        nodes.push(parent);
        getAncestors(parent);
      }
    };
    getAncestors(this);
    return nodes;
  }

  // get depth in system, including R node's
  getAbsoluteDepth() {
    const ancestors = this.ancestors();
    const depth = ancestors.length;
    const link = ancestors.length ? ancestors[ancestors.length - 1].link() : this.link();
    if (link) {
      return depth + link.getDepth();
    }
    return depth;
  }

  /* get all children & their children, etc of a node
     returns an array of Node instances
  */
  // should we add an optional argument indicating whether we want the root node included?
  descendants() {
    const nodes = [];

    const getDescendants = node => {
      for (const n of node.children()) {
        nodes.push(n);
        getDescendants(n);
      }
    };
    getDescendants(this);
    return nodes;
  }

  /*
   * return true if both nodes are in the same linear hierarchy from one another
   * i.e.
   *   A is parent of B, B is parent of C.
   *   C has common lineage with A
   *
   *   if A is parent of B1, and A is parent of B2, and B2 is parent of C2
   *   C2 does not have common lineage with B1
   */
  // only used in Link class
  hasCommonLineage(node) {
    const lineage1 = this.getLineage();
    const lineage2 = node.getLineage();
    return this._hasCommonLineage(lineage1, lineage2, this, node);
  }

  // same as above, but pass lineage's directly in
  _hasCommonLineage(lineage1, lineage2, node1, node2) {
    const longestLineage = lineage1.length > lineage2.length ? lineage1 : lineage2;

    // check if both nodes are present in the hierarchy of parent nodes
    const match1 = longestLineage.find(n => n.id == node1.id);
    const match2 = longestLineage.find(n => n.id == node2.id);

    if (match1 && match2) {
      return true;
    }
    return false;
  }

  // returns a Node instance
  getPrevSibling() {
    const parent = this.getParent();
    const orderIndex = this.orderIndex || 0;
    if (parent) {
      const prevNode = parent.children().filter(n => n.orderIndex < this.orderIndex)
        .sort((a, b) => b.orderIndex - a.orderIndex)[0];
      return prevNode;
    }
  }

  // returns a Node instance
  getNextSibling() {
    const parent = this.getParent();
    const orderIndex = this.orderIndex || 0;
    if (parent) {
      const prevNode = parent.children().filter(n => n.orderIndex > this.orderIndex)
        .sort((a, b) => a.orderIndex - b.orderIndex)[0];
      return prevNode;
    }
  }

  getSiblingsAndRecursiveChildren() {
    let siblingsAndChildren = [];

    const parent = this.getParent();

    const siblings = parent.children().filter(n => n.id !== this.id);

    siblingsAndChildren = siblingsAndChildren.concat(siblings);

    siblings.forEach(sibling => {
      siblingsAndChildren = siblingsAndChildren.concat(sibling.descendants());
    });

    siblingsAndChildren = siblingsAndChildren.concat(this.descendants());

    return siblingsAndChildren;
  }

  // return a bool indicating whether this is above otherNode in layout
  isAboveInLayout(otherNode) {
    const myLineage = this.getLineage();
    const otherLineage = otherNode.getLineage();
    if (myLineage.includes(otherNode)) {
      return false;
    } else if (otherLineage.includes(this)) {
      return true;
    } else {
      const mrca = myLineage.find(ancestor => otherLineage.includes(ancestor));
      if (mrca) {
        const myAncestralSibling = myLineage[myLineage.findIndex(el => el.id === mrca.id) + 1];
        const otherAncestralSibling = otherLineage[otherLineage.findIndex(el => el.id === mrca.id) + 1];
        return myAncestralSibling.orderIndex < otherAncestralSibling.orderIndex;
      } else {
        console.log("You called isAboveInLayout with two nodes that don't share a common ancestor");
      }
    }
  }

  childrenHaveChildren() {
    const children = this.children();
    let hasChildren = false;
    children.forEach(n => {
      if (n.children().length) {
        hasChildren = true;
      }
    });
    return hasChildren;
  }

  getTopRowLayoutParent() {
    if (!this.parentId) {
      return this.getLayoutName() === 'layout-row' ? this : NaN;
    }
    if (this.getParent() && this.getParent().getLayoutName() === 'layout-row') {
      return this.getParent().getTopRowLayoutParent();
    }
    if (this.getLayoutName() === 'layout-row') {
      return this;
    }
    return NaN;  // so that false return values won't be equal
  }

  getTopColLayoutParent() {
    if (!this.parentId) {
      return this.getLayoutName() === 'layout-col' ? this : NaN;
    }
    if (this.getParent() && this.getParent().getLayoutName() === 'layout-col') {
      return this.getParent().getTopColLayoutParent();
    }
    if (this.getLayoutName() === 'layout-col') {
      return this;
    }
    return NaN;  // so that false return values won't be equal
  }

  getTopNonFreehandParent() {
    if (!this.parentId) {
      return this;
    }
    if (this.getParent() && this.getParent().getLayoutName() !== 'layout-freehand') {
      return this.getParent().getTopNonFreehandParent();
    }
    return this;
  }

  getTopLevelParent() {
    if (!this.parentId) {
      return this;
    }
    if (this.getParent()) {
      return this.getParent().getTopLevelParent();
    }
    return this;
  }

  getWidthGoverningParent() {
    if (!this.parentId || this.getGoverningLayoutName() !== 'layout-row') {
      return this;
    }
    const parent = this.getParent();
    if (parent) {
      return parent.getWidthGoverningParent();
    }
    return this;
  }

  getTopLevelLinkParent() {
    const link = this.getTopLevelParent().link();
    if (!link) {
      return this.getTopLevelParent();
    }
    const linkTopLevelParentNode = link.getTopLevelParentNode();
    return linkTopLevelParentNode || this;
  }

  isTopLevel() {
    return this.getTopLevelParent() === this;
  }

  setTitle(title, titleMeta) {
    this.title = title;
    this.titleMeta = titleMeta;
    this.dirtyAncestors();
  }

  getTitle() {
    const title = new String(this.title || '');
    title.meta = this.titleMeta || [];
    return title;
  }

  setSummary(summary, summaryMeta) {
    this.summary = summary;
    this.summaryMeta = summaryMeta;
    this.dirtyAncestors();
  }

  getSummary() {
    const summary = new String(this.summary || '');
    summary.meta = this.summaryMeta || [];
    return summary;
  }

  setLibrarySlug(librarySlug) {
    this.librarySlug = librarySlug;
  }

  updateNode(updates) {
    for (const field in updates) {
      if (field == 'parentId') {
        // make sure this goes through validation
        this.setParentId(updates[field]);
      } else if (updates[field] !== undefined) {
        this[field] = updates[field];
      }
    }
    this.dirtyAncestors();
  }

  setParentId(id) {
    if (this.isValidParentId(id)) {
      // dirty old ancestors
      this.dirtyAncestors();

      // remove self from children of old parent
      if (this.parentId && this._store.getNode(this.parentId)) {
        const oldParent = this._store.getNode(this.parentId);
        oldParent.removeChild(this.id);
      }

      this.parentId = id;

      // if moving node to top level, remove hidden attribute
      if (!this.parentId) {
        this.hidden = false;
      } else {
        const parent = this._store.getNode(id);
        if (parent.getIsCollapsed() || parent.getHidden()) {
          this.hidden = true;
        } else {
          this.hidden = false;
        }
        parent.addChild(this.id);
      }

      // dirty new ancestors
      this.dirtyAncestors();
    } else {
      throw new IllegalMapOperationError("Tried to adopt an illegal parent");
    }
  }

  links() {
    const links = this._store.links.filter(l => l.fromNodeId === this.id || l.toNodeId === this.id);
    if (links && links) {
      return links;
    }
    return [];
  }

  link() {
    return this._store.links.find(l => l.linkNodeId === this.id);
  }

  // ignore elements in freehand containers
  descendantsLinksMinimal() {
    const seen = {};
    const links = [];
    const getDescendants = node => {
      if (!node) {
        console.error('undefined node in descendantsLinks');
        return;
      }
      if (node.links()) {
        for (let link of node.links()) {
          if (!seen[link.id]) {
            links.push(link);
            seen[link.id] = 1;

            if (link.linkNode()) {
              getDescendants(link.linkNode());
            }
          }
        }
      }
      if (node.getLayoutName() !== 'layout-freehand') {
        for (let n of node.children()) {
          getDescendants(n);
        }
      }
    };
    getDescendants(this);
    return links;
  }

  // what is this doing that's different from getMovingParts in util?
  descendantsLinks() {
    const seen = {};
    const links = [];
    const getDescendants = node => {
      if (!node) {
        console.error('undefined node in descendantsLinks');
        return;
      }
      if (node.links()) {
        for (let link of node.links()) {
          if (!seen[link.id]) {
            links.push(link);
            seen[link.id] = 1;

            if (link.linkNode()) {
              getDescendants(link.linkNode());
            }
          }
        }
      }
      for (let n of node.children()) {
        getDescendants(n);
      }
    };
    getDescendants(this);
    return links;
  }

  // get all links in the same family of nodes
  familyLinks() {
    const links = this.descendantsLinks();
    const filteredLinks = [];
    for (const link of links) {
      const fromNode = link.fromNode();
      const toNode = link.toNode();
      if (fromNode && toNode) {
        if (fromNode.hasCommonLineage(this) && toNode.hasCommonLineage(this)) {
          filteredLinks.push(link);
        }
      }
    }
    return filteredLinks;
  }

  // get all relationships within a given node's system
  // (including R's connected to R nodes within the system)
  getSystemRelationships() {
    const list = [];
    for (const link of this._store.links) {
      const parent = link.getTopLevelParentNode();
      if (parent && parent.id == this.id) {
        list.push(link);
      }
    }
    return list;
  }


  setAbsolutePosition({ x, y }) {
    this.absolutePosition.x = x;
    this.absolutePosition.y = y;
  }

  setPosition({ x, y }) {
    this.x = x;
    this.y = y;
  }

  setScale(scale) {
    this.scale = scale;
  }

  setLayoutName(layoutName) {
    this.layoutName = layoutName;
    this.dirtyAncestors();
    this.dirtyDescendants();
  }

  setBackgroundColor(color) {
    this.backgroundColor = color;
  }

  getBackgroundColor() {
    return this.backgroundColor || null;
  }

  setDisplayStyle(style) {
    this.displayStyle = style;
  }

  getDisplayStyle() {
    return this.displayStyle || null;
  }

  getLayoutName() {
    return this.layoutName || 'layout-row';
  }

  setIsCollapsed(bool) {
    this.isCollapsed = bool;
    this.dirtyAncestors();
  }

  getIsCollapsed() {
    return this.isCollapsed;
  }

  // get all ancestors in freehand mode
  getFreehandAncestors() {
    const nodes = this.getLineage();
    const list = nodes.filter(n => n.getLayoutName() == 'layout-freehand');
    if (this.getLayoutName() == 'layout-freehand') {
      list.push(this.instance ? this.instance : this);
    }
    return list;
  }


  // return true if any node up this node's chain of parents is collapsed
  getCollapsedAncestors() {
    const nodes = this.getLineage();
    const collapsedList = nodes.filter(n => n.isCollapsed);
    if (this.isCollapsed) {
      collapsedList.push(this);
    }
    return collapsedList;
  }

  // return true if both nodes share one collapsed ancestor
  sharesCollapsedAncestorWith(node) {
    const collapsed1 = this.getCollapsedAncestors();
    const collapsed2 = node.getCollapsedAncestors();
    for (const n1 of collapsed1) {
      if (collapsed2.find(n2 => n2.id == n1.id)) {
        return true;
      }
    }
    return false;
  }

  getHidden() {
    return this.hidden;
  }

  isShownNodeType() {
    if (this._store.isCardDraggingMode()) {
      if (this._cache.isShownNodeType) {
        return this._cache.isShownNodeType;
      }
      this._cache.isShownNodeType = this._isShownNodeType();
      return this._cache.isShownNodeType;
    }
    return this._isShownNodeType();
  }
  // if this is a relationship node and the line its on has been hidden
  // should be renamed?
  _isShownNodeType() {
    // if we're not a relationship node, return true;
    if (!this.link()) {
      return true;
    }

    if (this.link().showCollapsedArc()) {
      return false;
    }

    if (this.link().showArc()) {
      return true;
    }

    return false;
  }

  /* when a node is collapsed, flag its child nodes as hidden */
  setDescendantsHidden(bool) {
    this.children().forEach(n => {
      n.hidden = bool;
      if (!n.isCollapsed) {
        n.setDescendantsHidden(bool);
      }
    });
  }

  getFirstVisibleAncestorNode() {
    if (this._store.isCardDraggingMode()) {
      if (this._cache.getFirstVisibleAncestorNode) {
        return this._cache.getFirstVisibleAncestorNode;
      }
      this._cache.getFirstVisibleAncestorNode = this._getFirstVisibleAncestorNode();
      return this._cache.getFirstVisibleAncestorNode;
    }

    return this._getFirstVisibleAncestorNode();
  }

  /* when a node is collapsed, redirect its relationships to the first visible parent node */
  _getFirstVisibleAncestorNode() {
    /*
     * if the current node is hidden
     * or this node has a parent id
     *  and the top level parent is not a link node or its a link node that's not shown
     *  then find us the next visible node up the free we should use to attach a link endpoint to
     */
    if (this.hidden || (this.parentId && !this.getTopLevelParent().isShownNodeType())) {
      return this.getParent().getFirstVisibleAncestorNode();
    } else {

      /*
       *  if we're not a link node
       *  and we're not a link node on a collapsed link
       *  and our link is visible
       * then use this node
       */
      if (this.isShownNodeType()) {
        return this;
      }
      /*
       * if this is a relationship node on a line that is collapsed,
       * and this node is also the endpoint of another relationship
       * then redirect this endpoint to the collapsed from/to node
       */
      if (this.link()) {
        const fromNode = this.link().fromNode();
        const toNode = this.link().toNode();
        const firstNode = this.link().biasedToFrom ? fromNode : toNode;
        const secondNode = this.link().biasedToFrom ? toNode : fromNode;
        if (firstNode.hidden || !firstNode.isShownNodeType()) {
          return firstNode.getFirstVisibleAncestorNode();
        } else {
          // flip the bias
          this.link().biasedToFrom = !this.link().biasedToFrom;
          return secondNode.getFirstVisibleAncestorNode();
        }
      } else {
        console.error('getFirstVisibleAncestorPosition failed to find an endpoint');
        return this;
      }
    }
  }


  /* when a node is collapsed, redirect its relationships to the first visible parent node */
  getFirstVisibleAncestorPosition() {
    const firstVisibleNode = this.getFirstVisibleAncestorNode();

    if (this.id == firstVisibleNode.id) {
      return {
        x: firstVisibleNode.absolutePosition.x,
        y: firstVisibleNode.absolutePosition.y,
        top: firstVisibleNode.absolutePosition.y,
        bottom: firstVisibleNode.absolutePosition.y + firstVisibleNode.height,
        left: firstVisibleNode.absolutePosition.x,
        right: firstVisibleNode.absolutePosition.x + firstVisibleNode.width,
        width: firstVisibleNode.width,
        height: firstVisibleNode.height,
      };
    }
    const lineage = this.getLineage();
    let collapsedIndex = 0;

    for (let i = 0; i < lineage.length; i++) {
      if (lineage[i].id == firstVisibleNode.id) {
        collapsedIndex = i;
      }
    }

    let lastHiddenNode = lineage[collapsedIndex + 1];
    if (!lastHiddenNode || lastHiddenNode.orderIndex === undefined) {
      lastHiddenNode = firstVisibleNode;
    }


    let hardIndex = 0;
    if (lastHiddenNode.getParent()) {
      hardIndex = lastHiddenNode.getParent().children().findIndex(c => c.id === lastHiddenNode.id);
    }

    const [ childDotsVMargin, childDotsHMargin, childDotsInterval ] =
      firstVisibleNode.link() ? [ -20, 7, 7.2 ] : [ -20, 12, 7.2 ];

    return {
      x: firstVisibleNode.absolutePosition.x + (hardIndex || 0) * childDotsInterval + childDotsHMargin,
      y: firstVisibleNode.absolutePosition.y + firstVisibleNode.collapsedChildrenPosition.y + childDotsVMargin,
      //naive for now, no crazy childDotsMargins here
      top: firstVisibleNode.absolutePosition.y,
      bottom: firstVisibleNode.absolutePosition.y + firstVisibleNode.height,
      left: firstVisibleNode.absolutePosition.x,
      right: firstVisibleNode.absolutePosition.x + firstVisibleNode.width,
      width: firstVisibleNode.width,
      height: firstVisibleNode.height,
      _isAncestor: true,
    };
  }

  setHeight(height) {
    if (height) {
      this.height = height;
    }
  }

  setOrderIndex(index) {
    this.orderIndex = index;
    this.dirtyAncestors();
  }

  emancipateSelf({ x, y }) {
    const oldParentId = this.parentId;
    this._store.getNode(oldParentId).dirtyAncestors();
    this.setOrderIndex(0);
    this.setParentId(null);
    this.setAbsolutePosition({ x, y });
    this.setPosition({ x, y });
  }

  adoptParent(parentId) {
    this.setParentId(parentId);
  }

  /*
   * check that setting the given parent id is a valid action
   * - can't set to self
   * - can't set to one's own children
   */
  isValidParentId(id) {
    if (id == null) { return true; }
    if (id == this.parentId) { return true; }
    if (this.id == id) {
      // can't set self to parent
      console.log('attempted to set self as parent');
      return false;
    }
    if (!this._store.getNode(id)) {
      console.log('parent id does not exist');
      return false;
    }
    const desc = this.descendants();
    const match = desc.find(node => node.id == id);

    // can't set one of its children as its parent
    if (match) {
      console.log('parent id matches one of its children');
      return false;
    }

    if (this.link()) {
      // if this is a link node, cannot be adopted
      console.log('link nodes cannot adopt a parent');
      return false;
    }

    if (this.links()) {
      const matchingLinkNode = this.links().find(l => l.linkNode() && l.linkNode().id == id);
      if (matchingLinkNode) {
        console.log('parent id cannot be link node of its own link');
        return false;
      }

      for (const link of this.links()) {
        if (link.linkNode()) {
          const ldesc = link.linkNode().descendants();
          const match = ldesc.find(node => node.id == id);
          if (match) {
            console.log('parent id cannot be descendent of own link node');
            return false;
          }
        }
      }
    }

    return true;
  }

  getHeight() {
    return this.height;
  }

  applyMove({ dx, dy, appliedMoveLookup, canvas, viaPlaceLinkNode, onlyApplyToChildren }) {

    const applyDelta = (node, layoutName, move) => {

      let [clampX, clampY] = [0, 0];

      if (!appliedMoveLookup[node.id]) {

        if (move && layoutName != 'layout-freehand' && !viaPlaceLinkNode) {

          node.x += dx;
          node.y += dy;

        } else if (move && layoutName == 'layout-freehand' && !viaPlaceLinkNode) {

          node.x += dx;
          node.y += dy;

        }

        node.absolutePosition.x += dx;
        node.absolutePosition.y += dy;

        appliedMoveLookup[node.id] = 1;
        node.dirtyAncestors();
      }

      node.children().forEach(child => {
        applyDelta(child, node.getLayoutName());
      });
    };

    const parentNode = this.getParent();
    const layoutName = parentNode ? parentNode.getLayoutName() : null;
    if (onlyApplyToChildren) {
      this.children().forEach(child => {
        applyDelta(child, this.getLayoutName());
      });
    } else {
      applyDelta(this, layoutName, true);
    }
    return this.absolutePosition;
  }

  getNestedLevel() {
    let nestedLevel = 0;
    let node = this;
    while (node.parentId) {
      if (nestedLevel >= 3) {
        break;
      }
      nestedLevel += 1;
      node = node.getParent();
    }
    return nestedLevel;
  }

  addChild(childId) {
    if (!this.childrenIds.includes(childId)) {
      this.childrenIds.push(childId);
    }
  }

  removeChild(childId) {
    this.childrenIds = this.childrenIds.filter(x => x !== childId);
  }

  setChildrenDetached(detached) {
    this.childrenDetached = detached;
    this.dirtyAncestors();
  }

  getChildrenDetached() {
    return this.childrenDetached;
  }

  setJustification(justification) {
    this.justification = justification;
  }

  getJustification() {
    return this.justification || 'left';
  }

  setCollapsedChildrenPosition({ x, y }) {
    this.collapsedChildrenPosition.x = x;
    this.collapsedChildrenPosition.y = y;
  }

  setDimensions({ width, height }) {
    this.width = width;
    this.height = height;
  }

  setUserDimensions({ width, height }) {
    if (width !== undefined) {
      this.userWidth = width > 0 ? width : 0;
    }
    if (height !== undefined) {
      this.userHeight = height > 0 ? height : 0;
    }
  }

  setChildrenPosition({ x, y, userWidth, userHeight }) {
    this.childrenPosition.x = x || this.childrenPosition.x;
    this.childrenPosition.y = y || this.childrenPosition.y;
    if (Number.isFinite(userWidth)) {
      this.childrenPosition.userWidth = userWidth;
    }
    if (Number.isFinite(userHeight)) {
      this.childrenPosition.userHeight = userHeight;
    }
    this.dirtyAncestors();
  }

  getGoverningLayoutName() {
    const parentNode = this.getParent();
    const layoutName = parentNode ? parentNode.getLayoutName() : null;
    return layoutName;
  }

  doesRole(role) {
    return this.roles.concat([{ name: 'card' }]).find(({name}) => name === role);
  }

  removeRole(role) {
    if (!this._store.doesRole(role)) {
      console.log(`Map doesn't do role: ${role}`);
      return;
    }
    if (!this.doesRole(role)) {
      console.log(`${this.getTitle()} doesn't do ${role}`);
      return;
    }
    this.roles = this.roles.filter(({name}) => { name !== role; });
    return this.roles;
  }

  addRole(role) {
    if (!this._store.doesRole(role)) {
      console.log(`Map doesn't do role: ${role}`);
      return;
    }
    if (this.doesRole(role)) {
      console.log(`${this.getTitle()} already does ${role}`);
      return;
    }
    this.roles.push({ name: role });
    return { name: role };
  }

  addAttribute({ id, role='card', name, value }) {
    if (!this.doesRole(role)) {
      console.log(`${this.getTitle()} doesn't do ${role}`);
      return;
    }
    if (this.getAttributeByValue({role, name, value})) {
      // no dups
      return;
    }
    if (!this.attributes[role]) {
      this.attributes[role] = [];
    }
    if(!id) {
      id = uuid();
    }
    if (this.getAttribute({ role, id })) {
      console.log('already added attribute: ', id);
      return;
    }
    const roleAttr = this._store.getRoleAttribute(role, name);
    let attr;
    if (roleAttr) {
      const { description, displayName, peculiarities } = roleAttr;
      attr = { id, role, description, displayName, peculiarities, name, value };
    } else {
      attr = { id, role, name, value };
    }
    this.attributes[role].push(attr);
    return attr.id;
  }

  getAttribute({ role='card', id }) {
    if (!this.doesRole(role)) {
      console.log(`${this.getTitle()} doesn't do ${role}`);
      return;
    }
    const attr = this.attributes[role].find((a) => { return a.id === id; });
    if (!attr) {
      return;
    }
    return attr;
  }

  getAttributeByName({role='card', name }) {
    if (!this.attributes || !this.attributes[role]) return;
    return this.attributes[role].find((a) => a.name === name);
  }

  getAttributeByValue({role='card', name, value}) {
    if (!this.attributes || !this.attributes[role]) return;
    return this.attributes[role].find((a) => { return a.name === name && a.value === value; });
  }

  getAttributes() {
    if (!this.attributes || !this.attributes.card) return;
    const attributes = this.attributes['card'];
    return attributes;
  }

  editAttribute({ id, role='card', name, value }) {
    const attr = this.getAttribute({ role, id });
    if (attr) {
      attr.name = name;
      attr.value = value;
    }
    return id;
  }

  removeAttribute({ id, role='card' }) {
    const attr = this.getAttribute({ role, id });
    if (!attr) {
      return;
    }
    this.attributes[role] = this.attributes[role].filter(a => a.id !== id);
    return id;
  }

  hasAttributes() {
    if (!this.attributes) return false;
    if (Object.keys(this.attributes).length == 0) return false;
    for (const key in this.attributes) {
      if (this.attributes[key] && this.attributes[key].length) {
        return true;
      }
    }
    return false;
  }

  dump() {
    const { _store, _cache, ...newNode } = this;
    return newNode;
  };

}

class Link {

  constructor({ id, fromNodeId, toNodeId, linkNodeId, angleStartToR, angleEndToR, direction, color, lineType, lineWidth, lineCurvature, createdById }, _store) {

    if (!_store) throw new Error("we need a store");
    this._store = _store;

    const { angleStartToR: newAngleStartToR, angleEndToR: newAngleEndToR } =
      this.calculateNewAngles({ toNodeId, fromNodeId, angleEndToR, angleStartToR });

    this.id = id || uuid();
    this.fromNodeId = fromNodeId;
    this.toNodeId = toNodeId;
    this.linkNodeId = linkNodeId;
    this.createdById = createdById;
    this.direction = direction || 'none';
    this.angleStartToR = angleStartToR || newAngleStartToR;
    this.angleEndToR = angleEndToR || newAngleEndToR;
    this.color = color || DEFAULTS.LINK_COLOR;
    this.lineType = lineType || DEFAULTS.LINK_TYPE;
    this.lineWidth = lineWidth || DEFAULTS.LINK_WIDTH;
    this.lineCurvature = lineCurvature || DEFAULTS.LINK_CURVATURE;
    // controls where to redirect relationships to a link node, if the link node is hidden
    this.biasedToFrom = true;
  }

  calculateNewAngles({ toNodeId, fromNodeId, angleEndToR, angleStartToR }) {
    if (angleEndToR && angleStartToR) {
      return { angleStartToR, angleEndToR };
    }

    const fromNode = this._store.getNode(fromNodeId);
    const toNode = this._store.getNode(toNodeId);
    if (!fromNode || !toNode) {
      return { angleEndToR: Math.PI / 48, angleStartToR: Math.PI / -48 };
    }

    const isBetweenSiblings = (toNode.getTopLevelParent() === fromNode.getTopLevelParent() && toNode.getGoverningLayoutName() !== 'layout-freehand' && fromNode.getGoverningLayoutName() !== 'layout-freehand');

    const existingLinks = this._store.getLinksBetweenNodes(fromNodeId, toNodeId);
    if (existingLinks.length) {

      const biggestLink = existingLinks[0];
      let multiplier = 1;
      if (isBetweenSiblings) {
        multiplier = (toNode.isAboveInLayout(fromNode)) ? 1 : -1;
      }
      return {
        angleEndToR: biggestLink.angleEndToR + Math.PI / 48 * multiplier,
        angleStartToR: biggestLink.angleStartToR + Math.PI / -48 * multiplier,
      };

    } else if (isBetweenSiblings) {
      const multiplier = (toNode.isAboveInLayout(fromNode)) ? 1 : -1;

      return { angleEndToR: DEFAULT_SIBLING_LINK_ANGLE * multiplier, angleStartToR: DEFAULT_SIBLING_LINK_ANGLE * multiplier * -1 };
    } else {
      return { angleEndToR: Math.PI / 48, angleStartToR: Math.PI / -48 };
    }
  }

  fromNode() {
    return this._store.getNode(this.fromNodeId);
  }

  toNode() {
    return this._store.getNode(this.toNodeId);
  }

  linkNode() {
    if (this.linkNodeId) {
      return this._store.findNode(n => this.linkNodeId === n.id);
    }
  }

  setLinkNodeId(id) {
    if (this.isValidLinkNodeId(id)) {
      this.linkNodeId = id;

      // disconnect from parent if its set
      if (id != null) {
        this._store.getNode(id).setParentId(null);
      }
    } else {
      throw new IllegalMapOperationError("Tried to attach an illegal node to a link");
    }

  }

  isValidLinkNodeId(id) {
    if (id == null) { return true; }

    const node = this._store.getNode(id);
    if (!node) {
      console.log('node id does not exist');
      return false;
    }

    if (this.fromNodeId == id) {
      //console.log('cannot set from node as link node');
      return false;
    }

    if (this.toNodeId == id) {
      //console.log('cannot set to node as link node');
      return false;
    }

    // make sure the link node id doesn't match the from or to node of any of our children's links
    const descLinks = node.descendantsLinks();
    for (const link of descLinks) {
      if (link.id === this.id) {
        return false;
      }
    }

    return true;
  }

  // if this link is fully contained in a node, then get the top level parent it lives in
  getTopLevelParentNode() {
    const fromNode = this.fromNode();
    const toNode = this.toNode();
    const topLevelFromParent = fromNode.getTopLevelLinkParent();

    const topLevelToParent = toNode.getTopLevelLinkParent();

    // return the parent this link is contained in if we found one
    if (topLevelFromParent && topLevelToParent && topLevelFromParent.id == topLevelToParent.id) {
      return topLevelFromParent;
    } else {
      return;
    }
  }

  // how deep is this link nested based on depth of its highest level from & to nodes
  getDepth() {
    const fromNode = this.fromNode();
    const toNode = this.toNode();
    const fromAncestors = fromNode.ancestors();
    const toAncestors = toNode.ancestors();

    let topLevelFromParent = fromAncestors.length ? fromAncestors[fromAncestors.length - 1] : fromNode;
    let topLevelToParent = toAncestors.length ? toAncestors[toAncestors.length - 1] : toNode;

    let toDepth = toAncestors.length + 1; // +1 to account from from/to node
    let fromDepth = fromAncestors.length + 1;
    if (topLevelFromParent.link()) {
      fromDepth += topLevelFromParent.link().getDepth() + 1; // +1 to account for link
    }

    if (topLevelToParent.link()) {
      toDepth += topLevelToParent.link().getDepth() + 1;
    }
    return Math.max(fromDepth,toDepth);
  }

  isTopLevel() {
    if (!(this.fromNode() && this.toNode())) return;
    return !(this.fromNode().parentId) || (!this.toNode().parentId);
  }

  showCollapsedArc() {
    if (!this.fromNode()) {
      return false;
    }
    if (!this.toNode()) {
      return false;
    }
    const fromNode = this.fromNode();
    const toNode = this.toNode();
    const firstVisibleFromNode = fromNode.getFirstVisibleAncestorNode();
    const firstVisibleToNode = toNode.getFirstVisibleAncestorNode();

    // if the relationship is coming off 2 nodes that aren't collapsed, then show a regular arc
    if (fromNode.id == firstVisibleFromNode.id && toNode.id == firstVisibleToNode.id) {
      return false;
    }
    // if the parents of any of these nodes are collapsed, show a modified arc
    return true;
  }


  // if this arc is connecting a parent and child and the parent is collapsed, hide this arc
  // if this arc is connecting two nested parts inside a common parent, hide this arc
  showArc() {
    if (!this.fromNode()) {
      return false;
    }
    if (!this.toNode()) {
      return false;
    }

    if (this.fromNode().sharesCollapsedAncestorWith(this.toNode())) {
      return false;
    }
    return true;
  }

  setDirection(direction) {
    this.direction = direction;
  }

  setColor(color) {
    this.color = color;
  }

  setLinkType(type){
    this.lineType = type;
  }

  setLinkWidth(width){
    this.lineWidth = width;
  }

  setLinkCurvature(curvature){
    this.lineCurvature = curvature;
  }

  setAngles({ lNodePoint, angleStartToR, angleEndToR }) {
    if (angleStartToR && angleEndToR) {
      this.angleStartToR = angleStartToR;
      this.angleEndToR = angleEndToR;
      return { angleStartToR, angleEndToR };
    }
    const fromNode = this.fromNode();
    const toNode = this.toNode();
    const fromNodePosition = fromNode.getFirstVisibleAncestorPosition();
    const toNodePosition = toNode.getFirstVisibleAncestorPosition();

    const { fromPoint, toPoint } = this.getOffsetLinkPoints();

    let fromX = fromPoint.x;
    let fromY = fromPoint.y;
    let toX = toPoint.x;
    let toY = toPoint.y;

    const angles = getAnglesFromPoints(
      { x: fromX, y: fromY },
      { x: toX, y: toY },
      lNodePoint,
    );

    this.angleStartToR = angles[0];
    this.angleEndToR = angles[1];
    return { angleStartToR: angles[0], angleEndToR: angles[1] };
  }

  getThirdPoint() {
    const { fromPoint, toPoint } = this.getOffsetLinkPoints();
    return getPointFromAngles(
      fromPoint,
      this.angleStartToR,
      toPoint,
      this.angleEndToR
    );
  }


  getOffsetLinkPoints() {

    const fromNodePoint = this.fromNode().getFirstVisibleAncestorPosition();
    const toNodePoint = this.toNode().getFirstVisibleAncestorPosition();
    const fn = this.fromNode().getFirstVisibleAncestorNode();
    const tn = this.toNode().getFirstVisibleAncestorNode();
    let fp = Object.assign({}, fromNodePoint);
    let tp = Object.assign({}, toNodePoint);

    fp = this._overrideFromPoint || fp;
    tp = this._overrideToPoint || tp;

    const toWidth = tp._isAncestor ? 0 : tn.width || 0;
    const fromWidth = fp._isAncestor ? 0 : fn.width || 0;

    let fromXOffset = fromWidth / 2;
    let toXOffset = toWidth / 2;
    let fromYOffset = titleYOffset;
    let toYOffset = titleYOffset;

    if (fn.ancestors().includes(tn) || fn.descendants().includes(tn)) {

      // if one is inside the other stick their endpoints to the left edge
      fromXOffset = this.lineCurvature == 'elbow' ? 0 : titleXOffset;
      toXOffset = titleXOffset;

    } else if (tn.getTopRowLayoutParent() == fn.getTopRowLayoutParent()) {

      // if nodes are siblings or cousins prefer points inset from the left/right edges
      const cp = getPointFromAngles(fp, this.angleStartToR, tp, this.angleEndToR);
      if (cp.x > toNodePoint.x && cp.x > fromNodePoint.x) {
        // arc to the right
        fromXOffset = fromWidth - titleXOffset;
        toXOffset = toWidth - titleXOffset;
      } else {
        // arc to the left
        fromXOffset = titleXOffset;
        toXOffset = titleXOffset;
      }
    } else if (tn.parentId && tn.parentId == fn.parentId && tn.getGoverningLayoutName() != 'layout-freehand') {

      // if we're essentially column-wise siblings stick endpoints to the left edge
      fromXOffset = titleXOffset;
      toXOffset = titleXOffset;

    } else {

      // if one or both are list children prefer points along the left and right edges
      if (fn.getGoverningLayoutName() == 'layout-row') {
        if (fn.getParent().children().length > 1) {
          if (fp.x > tp.x) {
            fromXOffset = fromWidth * 0.1;
          } else {
            fromXOffset = fromWidth * 0.9;
          }
        }
      }
      if (tn.getGoverningLayoutName() == 'layout-row') {
        if (tn.getParent().children().length > 1) {
          if (tp.x > fp.x) {
            toXOffset = toWidth * 0.1;
          } else {
            toXOffset = toWidth * 0.9;
          }
        }
      }
    }

    if (fn.getTopLevelParent().link()) {
      fromXOffset -= fn.getTopLevelParent().width / 2;
      fromYOffset -= 17;
    }

    if (tn.getTopLevelParent().link()) {
      toXOffset -= tn.getTopLevelParent().width / 2;
      toYOffset -= 17;
    }

    // if we are dragging a from handle don't mess with it
    if (!this._overrideFromPoint) {
      fp.x += fromXOffset;
      fp.y += fromYOffset;
    }

    // if we are dragging a to handle don't mess with it
    if (!this._overrideToPoint) {
      tp.x += toXOffset;
      tp.y += toYOffset;
    }

    return { fromPoint: fp, toPoint: tp };
  }


  placeLinkNode() {
    if (!this.linkNodeId) return;
    const linkNode = this.linkNode();
    if (!linkNode) {
      console.log('could not find linkNode');
      return;
    }

    const fromNode = this.fromNode();
    const toNode = this.toNode();

    if (!this.fromNode()) {
      //console.error(`link ${this.id} is missing fromNode ${this.fromNodeId}`)
      return;
    }
    if (!this.toNode()) {
      //console.error(`link ${this.id} is missing toNode ${this.toNodeId}`)
      return;
    }


    const fromNodePosition = fromNode.getFirstVisibleAncestorPosition();
    const toNodePosition = toNode.getFirstVisibleAncestorPosition();


    if (fromNodePosition.x == undefined ||
      fromNodePosition.y == undefined ||
      toNodePosition.x == undefined ||
      toNodePosition.y == undefined) {
      console.error('undefined from or to node position for line', this.title, JSON.stringify({ fromNodePosition, toNodePosition }));
      return linkNode;
    }

    const { fromPoint, toPoint } = this.getOffsetLinkPoints();

    let fromX = fromPoint.x;
    let fromY = fromPoint.y;
    let toX = toPoint.x;
    let toY = toPoint.y;

    const { x, y } = getPointFromAngles(
      { x: fromX, y: fromY },
      this.angleStartToR,
      { x: toX, y: toY },
      this.angleEndToR
    );

    const _lastPosition = {
      x: linkNode.absolutePosition.x,
      y: linkNode.absolutePosition.y
    };

    linkNode.setPosition({ x, y });
    linkNode.setAbsolutePosition({ x, y });
    const dx = Math.round(x - _lastPosition.x);
    const dy = Math.round(y - _lastPosition.y);
    const appliedMoveLookup = {};

    linkNode.children().forEach(n => {
      n.applyMove({ dx, dy, appliedMoveLookup, viaPlaceLinkNode: true });
    });

    return linkNode;
  }

  addNode() {
    if (this.linkNodeId) return;
    const linkNodeId = this._store.addNode({
      title: 'Relationship Idea',
      summary: '',
    });
    this.linkNodeId = linkNodeId;
    this.placeLinkNode();
    return linkNodeId;
  }

  setEndpoints({ fromNodeId, toNodeId }) {
    const store = this._store;
    if (!store._validateLinkedNodes(fromNodeId, toNodeId, this.linkNodeId)) {
      throw new IllegalMapOperationError("Tried to set illegal link endpoints");
    }
    this.fromNodeId = fromNodeId;
    this.toNodeId = toNodeId;
  }

  dump() {
    // remove the _store property
    const { _store, ...newLink } = this;
    return newLink;
  };

}

class Perspective {
  constructor({ id, color, label, pointNodeUUID, orderIndex, nodes, links, members }) {
    this.id = id || uuid();
    this.label = label || "";
    this.color = color;
    this.pointNodeUUID = pointNodeUUID;
    this.orderIndex = orderIndex;
    this.members = members || { links: links || {}, nodes: nodes || {} };
    this.deleted = {
      links: {},
      nodes: {},
    };
  }

  // this does nothing if the node already exists
  // otherwise it adds an empty override object to members.nodes
  addMemberNode(node) {
    const id = node.id;
    if (!this.hasMemberNode(id)) {
      this.members.nodes[id] = {};
      delete this.deleted.nodes[id];
    }
  }

  // deletes id from this.members.nodes
  removeMemberNode(id) {
    if (this.members.nodes[id]) {
      this.deleted.nodes[id] = this.members.nodes[id];
    }
    delete this.members.nodes[id];
  }

  undeleteMemberNode(id) {
    if (this.deleted.nodes[id]) {
      this.members.nodes[id] = this.deleted.nodes[id];
      delete this.deleted.nodes[id];
    }
  }

  // this does nothing if the link already exists
  // otherwise it adds an empty override object to members.links
  addMemberLink(link) {
    const id = link.id;
    if (!this.hasMemberLink(id)) {
      this.members.links[id] = {};
      delete this.deleted.links[id];
    }
  }

  // deletes id from this.members.links
  removeMemberLink(id) {
    if (this.members.links[id]) {
      this.deleted.links[id] = this.members.links[id];
    }
    delete this.members.links[id];
  }

  undeleteMemberLink(id) {
    if (this.deleted.links[id]) {
      this.members.links[id] = this.deleted.links[id];
    } else {
      this.members.links[id] = {};
    }
    delete this.deleted.links[id];
  }

  setHidden(bool) {
    this.hidden = bool;
  }

  getPointNode() {
    if (!this.pointNodeUUID) {
      return null;
    }
    return store.getNode(this.pointNodeUUID);
  }

  // makes the id the point of the perspective
  addPoint(id) {
    this.pointNodeUUID = id;
  }

  isPointNode(id) {
    if (this.pointNodeUUID && this.pointNodeUUID == id) {
      return true;
    }
    return false;
  }

  getMembers() {
    return this.members;
  }

  getNonMembers() {
    const members = this.getMembers();
    if (members.nodes) {
      const memberIds = Object.keys(members.nodes);
      return store.filterNodes(node => {
        return memberIds.includes(id => id !== node.id);
      });
    } else {
      return [];
    }
  }

  hasMemberNode(id) {
    return Boolean(this.members.nodes[id]);
  }

  hasMemberLink(id) {
    return Boolean(this.members.links[id]);
  }

  updateColor(color) {
    this.color = color;
  }

  updateLabel(label) {
    this.label = label;
  }

  updateOrderIndex(orderIndex) {
    this.orderIndex = orderIndex;
  }

  getTitle() {
    if (this.pointNodeUUID) {
      const pointNode = store.getNode(this.pointNodeUUID);
      if (pointNode) {
        return pointNode.getTitle().toString();
      }
    }
    return this.label;
  }

  getRichTitle() {
    if (this.pointNodeUUID) {
      const pointNode = store.getNode(this.pointNodeUUID);
      if (pointNode) {
        const pointNodeTitle = pointNode.getTitle();
        const html = richText.decode({
          text: pointNodeTitle,
          meta: pointNodeTitle.meta
        });
        return html;
      }
    }
    return this.label;
  }

  updatePointNode(pointNodeUUID) {
    this.pointNodeUUID = pointNodeUUID;
  }

  // these do nothing if the node doesn't exist
  // (although that's not set in stone)

  // deprecate me
  overrideNodeTitle(id, title) {
    if (this.members.nodes[id]) {
      this.members.nodes[id].title = title;
    }
  }

  setAnnotationTitle(id, title) {
    if (this.members.nodes[id]) {
      this.members.nodes[id].title = title;
    }
  }

  setNodeAnnotationPosition({nodeId, x, y, gravity}) {
    if (this.members.nodes[nodeId]) {
      if (!this.members.nodes[nodeId].displayProperties) {
        this.members.nodes[nodeId].displayProperties = {};
      }
      this.members.nodes[nodeId].displayProperties.annotationPosition = {x, y, gravity};
    }
  }

  setNodeAnnotationDisplayProperties({nodeId, displayProperties}) {
    if (this.members.nodes[nodeId]) {
      this.members.nodes[nodeId].displayProperties = displayProperties;
    }
  }

  getNodeAnnotationPosition(nodeId) {
    if (this.members.nodes[nodeId] && this.members.nodes[nodeId].displayProperties) {
      return this.members.nodes[nodeId].displayProperties.annotationPosition || {};
    }
    return {};
  }

  getAnnotation(id) {
    if (this.members.nodes[id]) {
      return this.members.nodes[id].title || '';
    }
  }

  hasAnnotation(id) {
    return Boolean(this.members.nodes[id] && this.members.nodes[id].title);
  }

  update(updates) {
    if (updates.orderIndex !== undefined) {
      this.updateOrderIndex(updates.orderIndex);
    }

    if ('pointMapNodeUUID' in updates) {
      this.updatePointNode(updates.pointMapNodeUUID);
    }

    if (updates.label !== undefined) {
      this.updateLabel(updates.label);
    }

    if (updates.color !== undefined) {
      this.updateColor(updates.color);
    }
  }
}

class Waypoint {
  constructor({ id, createdByUserId, title, notes, orderIndex, nodeIds }, _store) {
    if (!_store) throw new Error("we need a store");
    this._store = _store;
    this.id = id || uuid();
    this.createdByUserId = createdByUserId;
    this.title = title || '';
    this.notes = notes || '';
    this.orderIndex = orderIndex;
    this.nodeIds = nodeIds;
  }

  setTitle({ title = '' }) {
    Object.assign(this, { title });
  }

  _validateNodeIds({ nodeIds = [] }) {
    // ensure these nodeIds are on the store
    const storeNodeIds = this._store.getNodesArray().map(n => n.id);
    return nodeIds.filter(nid => storeNodeIds.includes(nid));
  }

  getNodeIds() {
    const validNodeIds = this._validateNodeIds({ nodeIds: this.nodeIds });
    Object.assign(this, { nodeIds: validNodeIds });
    return this.nodeIds;
  }

  setNodeIds({ nodeIds = [] }) {
    const validNodeIds = this._validateNodeIds({ nodeIds });
    Object.assign(this, { nodeIds: validNodeIds });
  }

  dump() {
    const { _store, ...newWaypoint } = this;
    return newWaypoint;
  };

}

class Presentation {
  constructor({ id, title, createdByUserId, waypoints }, _store) {
    if (!_store) throw new Error("we need a store");
    this._store = _store;
    this.id = id || uuid();
    this.title = title || '';
    this.createdByUserId = createdByUserId;
    this.waypoints = waypoints ? waypoints.map(waypoint => new Waypoint(waypoint, _store)) : [];
    this.deleted = {
      waypoints: [],
    };
  }

  getWaypoint({ id }) {
    return this.waypoints.find(waypoint => waypoint.id === id);
  }

  getWaypoints() {
    return this.waypoints.sort((a, b) => a.orderIndex - b.orderIndex);
  }

  addWaypoint({ id, createdByUserId, title, notes, orderIndex, nodeIds }) {
    if (this.getWaypoint({ id })) {
      console.log('already added waypoint: ', id);
      return;
    }
    const waypoint = new Waypoint({ id, createdByUserId, title, notes, orderIndex, nodeIds }, this._store);
    this.waypoints.push(waypoint);
    return waypoint;
  }

  updateWaypoints({ waypoints }) {
    for (const waypoint of waypoints) {
      const stored = this.getWaypoint({ id: waypoint.id });
      stored.title = waypoint.title;
      stored.notes = waypoint.notes;
      stored.nodeIds = waypoint.nodeIds;
    }
  }

  removeWaypoint({ id }) {
    const deleteWaypoint = this.getWaypoint({ id });
    if (deleteWaypoint) {
      this.deleted.waypoints.push(deleteWaypoint);
      this.waypoints = this.waypoints.filter(waypoint => waypoint.id !== id);
    }
  }

  clearWaypoints() {
    this.deleted.waypoints = this.waypoints;
    this.waypoints = [];
  }

  updateWaypointOrderIndexes(idIndexMappings) {
    this.waypoints = idIndexMappings.map(({ id, orderIndex }) => {
      const waypoint = this.getWaypoint({ id });
      Object.assign(waypoint, { orderIndex });
      return waypoint;
    });
  }

  removeNodesFromWaypoints({ nodes }) {
    const updatedWaypoints = []; // { id, nodeIds, prevNodeIds }
    const removedWaypoints = []; // waypoints
    const nodeIds = nodes.map(n => { return n.id; });
    const nodeTitles = nodes.map(n => { return n.getTitle(); });

    this.waypoints.filter(waypoint => {
      return waypoint.nodeIds.find(nodeId => {
        return nodeIds.includes(nodeId);
      });
    }).forEach(waypoint => {
      const prevNodeIds = waypoint.nodeIds;
      waypoint.nodeIds = waypoint.nodeIds.filter(nodeId => { return !nodeIds.includes(nodeId); });
      if (waypoint.nodeIds.length === 0 && waypoint.notes.length === 0 && nodeTitles.includes(waypoint.title)) {
        waypoint.nodeIds = prevNodeIds; // for undo/redo
        removedWaypoints.push(waypoint);
        this.removeWaypoint({ id: waypoint.id });
      } else {
        updatedWaypoints.push({ id: waypoint.id, prevNodeIds, nodeIds: waypoint.nodeIds });
      }
    });

    return { updatedWaypoints, removedWaypoints };
  }

  updateWaypointsTitle({ nodeId, prevTitle, title }) {
    return this.waypoints
      .filter(({ nodeIds, title: waypointTitle }) =>
        nodeIds.length === 1 && nodeIds.includes(nodeId) && waypointTitle === prevTitle
      )
      .map(waypoint => {
        waypoint.setTitle({ title });
        return waypoint;
      });
  }

  /*
    return waypoint number that this element appears in
  */
  getWaypointIndexOfElement({ elementId }) {
    const waypoints = this.getWaypoints();
    if (!waypoints || !waypoints.length) return -1;
    const index = waypoints.findIndex(wp => wp.getNodeIds().includes(elementId) );
    return index;
  }

  dump() {
    const { _store, ...newPresentation } = this;
    const newWaypoints = newPresentation.waypoints.map(wp => wp.dump());
    newPresentation.waypoints = newWaypoints;

    return newPresentation;
  };

}

class Region {
  constructor({ id, top, bottom, left, right, color, title }) {
    this.id = id || uuid();
    this.color = color;
    this.top = top;
    this.bottom = bottom;
    this.left = left;
    this.right = right;
    this.title = title;
    this.horizontalMove = 0;
    this.verticalMove = 0;

    this.unsnappedRect = {
      top: this.top,
      bottom: this.bottom,
      left: this.left,
      right: this.right,
    };
  }

  update(updates) {
    for (const update in updates) {
      if (this[update] !== undefined) {
        this[update] = updates[update];
      } else {
        console.log('bad update',update);
      }
    }
  }

  applyunSnappedResize({ top, left, bottom, right }) {
    this.unsnappedRect = {
      top,
      bottom,
      left,
      right,
    };
  }

  applyunSnappedMove({ dx, dy }) {
    this.unsnappedRect = {
      top: this.unsnappedRect.top + dy ,
      bottom: this.unsnappedRect.bottom + dy,
      left: this.unsnappedRect.left + dx,
      right: this.unsnappedRect.right + dx,
    };
  }

  setColor(color) {
    this.color = color;
  }

  setTitle(title) {
    this.title = title;
  }

  dump() {
    const { unsnappedRect, horizontalMove, verticalMove, ...newRegion} = this;
    return newRegion;
  };
}

class Comment {
  constructor({ id, displayProperties, commentText, parentId, userId, createdTime, isDeleted }, _store) {
    this.id = id || uuid();
    this.displayProperties = displayProperties;
    this.commentText = commentText;
    this.parentId = parentId;
    this.userId = userId;
    this.createdTime = createdTime;
    this.isDeleted = isDeleted;
    this._store = _store;
  }

  setText(text) {
    this.commentText = text;
  }
  getText() {
    return this.isDeleted ? 'This comment has been deleted.' : this.commentText;
  }
  getParentId() {
    return this.parentId;
  }
  getParent() {
    if (!this.parentId) return;
    const parent = this._store.getComment(this.parentId);
    return parent;
  }
  getTopLevelComment() {
    if (!this.parentId) return this;
    const parent = this._store.getComment(this.parentId);
    if (!parent) return this;
    return parent.getTopLevelComment();
  }
  getTarget() {
    return this.displayProperties.target;
  }
  getPosition() {
    return this.displayProperties.position;
  }
  setPosition({x, y}) {
    this.displayProperties.position = {
      x,
      y
    };
  }
  getNodeId() {
    return this.displayProperties.mapNodeUUID;
  }
  getSelection() {
    return this.displayProperties.selection;
  }
  getUserId() {
    return this.userId;
  }
  setVote(userId,value) {
    if (!this.displayProperties.votes) {
      this.displayProperties.votes = {};
    }
    this.displayProperties.votes[userId] = value;
  }
  delete() {
    this.isDeleted = true;
  }
  undelete() {
    this.isDeleted = false;
  }

  resolve() {
    this.displayProperties.resolved = true;
  }

  unresolve() {
    this.displayProperties.resolved = false;
  }

  isResolved() {
    return Boolean(this.displayProperties.resolved);
  }

  getReplyCount() {
    const replies = this._store.getReplyComments(this.id);
    return replies.length;
  }

  // if comment has no replies and is deleted, return true;
  // if comment has replies and it and all its replies are deleted, return true
  isPurged() {
    if (!this.isDeleted) return false;
    const replies = this._store.getReplyComments(this.id);
    if (!replies || !replies.length) return true;
  }

  dump() {
    const { _store, ...comment } = this;
    return comment;
  };
}

export {
  MapStore,
  Node,
  Link,
  Region,
};



// WEBPACK FOOTER //
// ./starling/src/store.js